import { useEventListener } from '@vueuse/core'
import type { Ref, MaybeRefOrGetter } from 'vue'

const DRAG_THRESHOLD = 5

export function useDragScroll(
  target: Ref<HTMLElement | null>,
  enabled: MaybeRefOrGetter<boolean> = true,
) {
  const isDragging = ref(false)

  let pointerDown = false
  let pointerId = -1
  let startX = 0
  let startY = 0
  let startScrollLeft = 0
  let startScrollTop = 0

  function reset() {
    pointerDown = false
    isDragging.value = false
  }

  // Cancel drag when enabled becomes false (e.g. block drag starts)
  watch(() => toValue(enabled), (val) => {
    if (!val) reset()
  })

  useEventListener(target, 'pointerdown', (e: PointerEvent) => {
    if (e.pointerType === 'touch') return
    if (!toValue(enabled)) return
    if (!target.value) return

    pointerDown = true
    pointerId = e.pointerId
    startX = e.clientX
    startY = e.clientY
    startScrollLeft = target.value.scrollLeft
    startScrollTop = target.value.scrollTop
  })

  useEventListener(window, 'pointermove', (e: PointerEvent) => {
    if (!pointerDown || e.pointerId !== pointerId) return
    if (!toValue(enabled) || !target.value) {
      reset()
      return
    }

    const dx = e.clientX - startX
    const dy = e.clientY - startY

    if (!isDragging.value) {
      if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return
      isDragging.value = true
    }

    target.value.scrollLeft = startScrollLeft - dx
    target.value.scrollTop = startScrollTop - dy
  }, { passive: true })

  useEventListener(window, 'pointerup', (e: PointerEvent) => {
    if (!pointerDown || e.pointerId !== pointerId) return
    reset()
  })

  useEventListener(window, 'pointercancel', (e: PointerEvent) => {
    if (!pointerDown || e.pointerId !== pointerId) return
    reset()
  })

  return { isDragging }
}
